import { ApiResponse } from "src/types/OrganizationDTO";
import api from "../core/axios";

export type AgentStatus = 'AVAILABLE' | 'BUSY' | 'AWAY' | 'OFFLINE';

export interface AgentStatusResponse {
    userId: string;
    status: AgentStatus;
    lastUpdated: string;
    reason?: string;
    activeConversations: number;
    role: string;
}

export interface AgentStatusHistory {
    id: string;
    userId: string;
    status: AgentStatus;
    createdAt: string;
    reason?: string;
}

// Get current user's agent status
export const fetchMyAgentStatus = async (): Promise<AgentStatusResponse> => {
    try {
        const response = await api.get<ApiResponse<AgentStatusResponse>>('/agent-status/my-status');
        return response.data.data || {
            userId: '',
            status: 'AVAILABLE',
            lastUpdated: new Date().toISOString(),
            activeConversations: 0,
            role: 'agent'
        };
    } catch (error) {
        console.error('Failed to fetch agent status:', error);
        throw error; // Let component handle error
    }
};

// Update current user's agent status
export const updateMyAgentStatus = async (
    status: AgentStatus,
    reason?: string
): Promise<AgentStatusResponse> => {
    try {
        const response = await api.put<ApiResponse<AgentStatusResponse>>('/agent-status/my-status', {
            status,
            reason
        });

        // Check if data exists
        if (!response.data.data) {
            throw new Error('No data received from server');
        }

        return response.data.data;
    } catch (error) {
        console.error('Failed to update agent status:', error);
        throw error;
    }
};

// Get all available agents in organization
export const fetchAvailableAgents = async (): Promise<AgentStatusResponse[]> => {
    try {
        const response = await api.get<ApiResponse<AgentStatusResponse[]>>('/agent-status/available');
        return response.data.data || [];
    } catch (error) {
        console.error('Failed to fetch available agents:', error);
        return [];
    }
};

// Get status history for current user
export const fetchMyStatusHistory = async (
    from?: string,
    to?: string
): Promise<AgentStatusHistory[]> => {
    try {
        const params = new URLSearchParams();
        if (from) params.append('from', from);
        if (to) params.append('to', to);

        const response = await api.get<ApiResponse<AgentStatusHistory[]>>(
            `/agent-status/history?${params.toString()}`
        );
        return response.data.data || [];
    } catch (error) {
        console.error('Failed to fetch status history:', error);
        return [];
    }
};